/**
 * Nome da caixa abaixo do ícone no mapa (texto branco com contorno preto).
 */

/** Área mínima reservada para o nome abaixo do ícone (unidades do viewBox). */
export const MAP_MARKER_NAME_VIEWBOX_PADDING = 18;

/** Tamanho alvo do nome na tela (px), após escala do marcador. */
const TARGET_NAME_SCREEN_PX = 11;

/** Distância entre a base do ícone e o início do nome (unidades do viewBox). */
const NAME_GAP_FROM_ICON = 15;

function decodeSvgDataUrl(svgDataUrl: string): string | null {
  try {
    const encoded = svgDataUrl.replace(/^data:image\/svg\+xml(?:;charset=UTF-8)?,/, '');
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

function escapeSvgText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function truncateName(name: string, maxLength = 24): string {
  const trimmed = name.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 1)}…`;
}

export function parseSvgViewBoxSize(svgDataUrl: string): { width: number; height: number } | null {
  const svgString = decodeSvgDataUrl(svgDataUrl);
  if (!svgString) return null;

  const viewBoxMatch = svgString.match(/viewBox=["']([^"']+)["']/);
  if (viewBoxMatch) {
    const parts = viewBoxMatch[1].trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every(n => Number.isFinite(n))) {
      return { width: parts[2], height: parts[3] };
    }
  }

  const widthMatch = svgString.match(/\bwidth=["'](\d+(?:\.\d+)?)/);
  const heightMatch = svgString.match(/\bheight=["'](\d+(?:\.\d+)?)/);
  if (widthMatch && heightMatch) {
    return { width: Number(widthMatch[1]), height: Number(heightMatch[1]) };
  }

  return null;
}

export function getExtraDisplayHeightForNameLabel(
  displayHeight: number,
  viewBoxHeight: number,
  namePadding: number = MAP_MARKER_NAME_VIEWBOX_PADDING,
): number {
  return displayHeight * (namePadding / viewBoxHeight);
}

/**
 * Adiciona o nome abaixo do ícone no SVG. O anchor do marcador permanece no ícone original.
 * @param displayWidth largura em px do marcador no mapa (scaledSize.width)
 */
export function appendNameBelowIconSvg(
  svgDataUrl: string,
  name: string,
  displayWidth: number,
): { url: string; viewBoxHeightBefore: number; namePadding: number; displayWidthScale: number } {
  const svgString = decodeSvgDataUrl(svgDataUrl);
  if (!svgString) return { url: svgDataUrl, viewBoxHeightBefore: 0, namePadding: 0, displayWidthScale: 1 };

  const viewBox = parseSvgViewBoxSize(svgDataUrl);
  if (!viewBox) return { url: svgDataUrl, viewBoxHeightBefore: 0, namePadding: 0, displayWidthScale: 1 };

  const openTagEnd = svgString.indexOf('>', svgString.indexOf('<svg'));
  if (openTagEnd === -1) {
    return { url: svgDataUrl, viewBoxHeightBefore: viewBox.height, namePadding: 0, displayWidthScale: 1 };
  }

  const innerContent = svgString.substring(openTagEnd + 1, svgString.lastIndexOf('</svg>'));
  const fontSize = Math.max(12, Math.round((TARGET_NAME_SCREEN_PX * viewBox.width) / Math.max(displayWidth, 1)));
  const strokeWidth = Math.max(1.6, fontSize * 0.22);
  const safeName = escapeSvgText(truncateName(name));

  const textY = viewBox.height + NAME_GAP_FROM_ICON;
  const namePadding = NAME_GAP_FROM_ICON + Math.max(
    MAP_MARKER_NAME_VIEWBOX_PADDING - NAME_GAP_FROM_ICON,
    Math.ceil(fontSize * 1.3 + strokeWidth * 2),
  );

  // Espaço horizontal: nomes longos não podem ultrapassar a largura do viewBox original.
  const estimatedTextWidth = safeName.length * fontSize * 0.58;
  const horizontalMargin = strokeWidth * 2 + 6;
  const newWidth = Math.max(viewBox.width, Math.ceil(estimatedTextWidth + horizontalMargin));
  const iconOffsetX = (newWidth - viewBox.width) / 2;
  const newHeight = viewBox.height + namePadding;

  const wrapped =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${newWidth} ${newHeight}" width="${newWidth}" height="${newHeight}" overflow="visible">` +
    `<g transform="translate(${iconOffsetX}, 0)">${innerContent}</g>` +
    `<text x="${newWidth / 2}" y="${textY}" ` +
    `fill="#ffffff" stroke="#000000" stroke-width="${strokeWidth}" paint-order="stroke fill" ` +
    `font-family="Arial,Helvetica,sans-serif" font-size="${fontSize}" font-weight="bold" ` +
    `text-anchor="middle" dominant-baseline="hanging">${safeName}</text>` +
    `</svg>`;

  return {
    url: 'data:image/svg+xml,' + encodeURIComponent(wrapped),
    viewBoxHeightBefore: viewBox.height,
    namePadding,
    displayWidthScale: newWidth / viewBox.width,
  };
}
